import { useEffect } from 'react';

const CRITICAL_IMAGE_CONTAINER_SELECTOR = [
  '.rev_slider_wrapper',
  '.slider-outer',
  '.main-banner',
  '.wt-bnr-inr',
  '.header-logo'
].join(',');

function isLikelyLcpCandidate(image) {
  const rect = image?.getBoundingClientRect?.();
  if (!rect) {
    return false;
  }

  const area = rect.width * rect.height;
  return area >= 70000 && rect.top <= window.innerHeight * 1.1;
}

function shouldKeepEager(image) {
  if (!image || image.closest(CRITICAL_IMAGE_CONTAINER_SELECTOR)) {
    return true;
  }

  if (image.classList.contains('rev-slidebg') || image.hasAttribute('data-priority')) {
    return true;
  }

  const rect = image.getBoundingClientRect();
  return rect.top <= window.innerHeight * 1.15;
}

function optimizeImage(image) {
  if (!image || image.dataset.lazyOptimized === '1') {
    return;
  }

  if (shouldKeepEager(image)) {
    image.loading = 'eager';
    if (isLikelyLcpCandidate(image) && !document.querySelector('img[data-lcp-priority="1"]')) {
      image.dataset.lcpPriority = '1';
      image.setAttribute('fetchpriority', 'high');
    } else if (!image.getAttribute('fetchpriority')) {
      image.setAttribute('fetchpriority', 'auto');
    }
  } else {
    if (!image.getAttribute('loading')) {
      image.loading = 'lazy';
    }
    if (!image.getAttribute('fetchpriority')) {
      image.setAttribute('fetchpriority', 'low');
    }
  }

  if (!image.getAttribute('decoding')) {
    image.setAttribute('decoding', 'async');
  }

  image.dataset.lazyOptimized = '1';
}

function optimizeAllImages() {
  document.querySelectorAll('img').forEach(optimizeImage);
}

function LazyImageManager() {
  useEffect(() => {
    optimizeAllImages();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.tagName === 'IMG') {
            optimizeImage(node);
            return;
          }

          node.querySelectorAll?.('img').forEach(optimizeImage);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('load', optimizeAllImages);

    return () => {
      observer.disconnect();
      window.removeEventListener('load', optimizeAllImages);
    };
  }, []);

  return null;
}

export default LazyImageManager;
